import { Button, ScrollView, XStack, YStack, useWindowDimensions, Tooltip, Paragraph, Group } from "tamagui";
import { Check, ChevronFirst, ChevronLeft, ChevronRight, ChevronLast, Trash2, Save, Upload } from "@tamagui/lucide-icons-2";
import { Platform, StyleSheet } from "react-native-web";
import { SimpleText } from "../bento-controlled/elements/texts/index.mjs";
import { useEffect, useRef, memo, useState } from "react";
import { DropDownPickerView } from "../bento-controlled/elements/dropdowns/index.mjs";
import { color, TZABrandGuidelines, useResizeObserver } from "@my/util";
import { XPProgressBar } from "./XPProgressBar.mjs";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
const StepButton = memo(({
  stepNumber,
  isActive,
  handleStepChange,
  label
}) => /* @__PURE__ */jsxs(Tooltip, {
  groupId: stepNumber + "",
  placement: "top",
  delay: 50,
  children: [/* @__PURE__ */jsx(Tooltip.Trigger, {
    children: /* @__PURE__ */jsx(Button, {
      unstyled: true,
      marginHorizontal: 3,
      onPress: () => handleStepChange(stepNumber),
      children: /* @__PURE__ */jsx(YStack, {
        style: [styles.circle, isActive && styles.activeCircle],
        hoverStyle: {
          backgroundColor: "rgb(220, 220, 220)"
        },
        children: isActive ? /* @__PURE__ */jsx(SimpleText, {
          style: styles.circleText,
          children: stepNumber
        }) : /* @__PURE__ */jsx(Check, {
          size: 20,
          color: "#85C19D"
        })
      })
    })
  }), /* @__PURE__ */jsxs(Tooltip.Content, {
    backgroundColor: "white",
    enterStyle: {
      x: 0,
      y: -5,
      opacity: 0,
      scale: 0.9
    },
    exitStyle: {
      x: 0,
      y: -5,
      opacity: 0,
      scale: 0.9
    },
    scale: 1,
    x: 0,
    y: 0,
    opacity: 1,
    transition: ["quick", {
      opacity: {
        overshootClamping: true
      }
    }],
    children: [/* @__PURE__ */jsx(Tooltip.Arrow, {
      backgroundColor: "white"
    }), /* @__PURE__ */jsx(Paragraph, {
      size: "$2",
      lineHeight: "$1",
      color: "black",
      children: label
    })]
  })]
}));
const ActiveSteps = memo(({
  currentStep,
  handleStepChange,
  stepLabels
}) => /* @__PURE__ */jsx(Fragment, {
  children: Array.from({
    length: currentStep
  }).map((_, index) => {
    const stepNumber = index + 1;
    return /* @__PURE__ */jsx(StepButton, {
      stepNumber,
      label: stepLabels[stepNumber - 1],
      isActive: currentStep === stepNumber,
      handleStepChange
    }, stepNumber);
  })
}));
const UpcomingSteps = memo(({
  currentStep,
  stepLabels,
  handleStepChange
}) => /* @__PURE__ */jsx(Fragment, {
  children: Array.from({
    length: stepLabels.length - currentStep
  }).map((_, index) => {
    const stepNumber = currentStep + index + 1;
    return /* @__PURE__ */jsx(StepButton, {
      stepNumber,
      label: stepLabels[stepNumber - 1],
      isActive: true,
      handleStepChange
    }, stepNumber);
  })
}));
const ProgressStepper = ({
  currentStep,
  handleStepChange,
  stepLabels,
  progressValue,
  sidebarState,
  buttonFns,
  minimal
}) => {
  const {
    width
  } = useWindowDimensions();
  const [containerDimensions, containerDimensionProps] = useResizeObserver();
  const scrollViewRef = useRef(null);
  useEffect(() => {
    if (scrollViewRef.current) {
      const stepWidth = 45;
      const offset = Math.round((currentStep - 1) * stepWidth - width / 2 + stepWidth / 2);
      scrollViewRef.current.scrollTo({
        x: offset > 0 ? offset : 0,
        animated: true
      });
    }
  }, [currentStep, width]);
  const showNavButtons = containerDimensions.width >= 540;
  const leftNavButtons = /* @__PURE__ */jsxs(Fragment, {
    children: [/* @__PURE__ */jsx(Group.Item, {
      children: /* @__PURE__ */jsx(Button, {
        disabled: currentStep === 1,
        onPress: () => handleStepChange(1),
        size: "$3",
        icon: /* @__PURE__ */jsx(ChevronFirst, {})
      })
    }), /* @__PURE__ */jsx(Group.Item, {
      children: /* @__PURE__ */jsx(Button, {
        disabled: currentStep === 1,
        onPress: () => handleStepChange(currentStep - 1),
        size: "$3",
        icon: /* @__PURE__ */jsx(ChevronLeft, {})
      })
    })]
  });
  const rightNavButtons = /* @__PURE__ */jsxs(Fragment, {
    children: [/* @__PURE__ */jsx(Group.Item, {
      children: /* @__PURE__ */jsx(Button, {
        disabled: currentStep === stepLabels.length,
        onPress: () => handleStepChange(currentStep + 1),
        size: "$3",
        icon: /* @__PURE__ */jsx(ChevronRight, {})
      })
    }), /* @__PURE__ */jsx(Group.Item, {
      children: /* @__PURE__ */jsx(Button, {
        disabled: currentStep === stepLabels.length,
        onPress: () => handleStepChange(stepLabels.length),
        size: "$3",
        icon: /* @__PURE__ */jsx(ChevronLast, {})
      })
    })]
  });
  return /* @__PURE__ */jsxs(YStack, {
    ...containerDimensionProps,
    children: [minimal ? null : /* @__PURE__ */jsxs(XStack, {
      style: styles.container,
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      padding: 16,
      children: [showNavButtons ? /* @__PURE__ */jsx(Group, {
        size: "$3",
        orientation: "horizontal",
        children: leftNavButtons
      }) : null, containerDimensions.width > 980 ? /* @__PURE__ */jsxs(ScrollView, {
        horizontal: true,
        ref: scrollViewRef,
        showsHorizontalScrollIndicator: false,
        style: {
          width: "90%",
          marginLeft: 5,
          marginRight: 5
        },
        contentContainerStyle: styles.stepperContainer,
        className: "steppersScrollView",
        children: [/* @__PURE__ */jsxs(XStack, {
          style: styles.steps,
          children: [/* @__PURE__ */jsx(ActiveSteps, {
            currentStep,
            stepLabels,
            handleStepChange
          }), /* @__PURE__ */jsx(SimpleText, {
            style: styles.stepLabel,
            children: stepLabels[currentStep - 1]
          })]
        }), /* @__PURE__ */jsx(XStack, {
          style: styles.steps,
          children: /* @__PURE__ */jsx(UpcomingSteps, {
            currentStep,
            stepLabels,
            handleStepChange
          })
        })]
      }) : /* @__PURE__ */jsx(XStack, {
        className: "steppersSelect",
        alignItems: "center",
        style: {
          /* width: '98%', */
          flexGrow: 1,
          padding: 0,
          /* margin: '5px', */
          alignItems: "center"
        },
        children: /* @__PURE__ */jsx(DropDownPickerView, {
          setValue: stepString => handleStepChange(parseInt(stepString)),
          value: currentStep,
          items: stepLabels.map((label, idx) => ({
            label: `${idx + 1}. ${label}`,
            value: idx + 1
          })),
          DropDownContainerStyle: {
            width: "100%",
            padding: 0
            /* , margin: '10px 0' */
          },
          ContainerStyles: [{
            height: 36,
            overflow: "hidden",
            alignItems: "center"
          }, {
            height: 36
            /* , marginTop: -10 */
          }, {
            /* height: 36 */
          }],
          TriggerStyle: {
            /* width: '100%' */
          },
          ViewStyle: {
            padding: 0
          },
          native: true
        })
      }), showNavButtons ? /* @__PURE__ */jsx(Group, {
        size: "$3",
        orientation: "horizontal",
        children: rightNavButtons
      }) : null]
    }), /* @__PURE__ */jsx(XPProgressBar, {
      value: progressValue,
      primaryColor: TZABrandGuidelines.colorsMap.primaryGreen,
      secondaryColor: TZABrandGuidelines.colorsMap.secondaryMustard,
      height: 6
    }), /* @__PURE__ */jsx(XStack, {
      style: {
        alignItems: "center",
        justifyContent: minimal ? "center" : "initial"
      },
      gap: 5,
      children: /* @__PURE__ */jsxs(Group, {
        size: "$3",
        orientation: "horizontal",
        gap: minimal ? 0 : 5,
        marginLeft: minimal ? 0 : "auto",
        marginTop: 10,
        borderRadius: 9,
        children: [minimal ? leftNavButtons : null, buttonFns.discard.disabled ? null : /* @__PURE__ */jsx(Group.Item, {
          children: /* @__PURE__ */jsx(Button, {
            disabled: buttonFns.discard.disabled,
            ...(!buttonFns.discard.disabled ? {
              backgroundColor: "#ff3333",
              color: "white",
              fontWeight: 500,
              hoverStyle: {
                backgroundColor: TZABrandGuidelines.colorsMap.secondarySalmon
              }
            } : {
              color: "#ff3333"
            }),
            onPress: buttonFns.discard.fn,
            size: "$3",
            icon: Trash2,
            children: minimal ? "" : "Discard Changes"
          })
        }), buttonFns.save.disabled ? null : /* @__PURE__ */jsx(Group.Item, {
          children: /* @__PURE__ */jsx(Button, {
            ...(!buttonFns.save.disabled ? {
              backgroundColor: TZABrandGuidelines.colorsMap.primaryGreen,
              color: "white",
              fontWeight: 500,
              hoverStyle: {
                backgroundColor: color.lightGreen
              }
            } : {
              color: TZABrandGuidelines.colorsMap.primaryGreen
            }),
            onPress: buttonFns.save.fn,
            size: "$3",
            icon: Save,
            children: minimal ? "" : "Save Changes"
          })
        }), buttonFns.submit && !buttonFns.submit.disabled ? /* @__PURE__ */jsx(Group.Item, {
          children: /* @__PURE__ */jsx(Button, {
            ...(!buttonFns.submit.disabled ? {
              backgroundColor: TZABrandGuidelines.colorsMap.primaryGreen,
              color: "white",
              fontWeight: 500,
              hoverStyle: {
                backgroundColor: color.lightGreen
              }
            } : {
              color: TZABrandGuidelines.colorsMap.primaryGreen
            }),
            onPress: buttonFns.submit.fn,
            size: "$3",
            icon: Upload,
            children: "Submit"
          })
        }) : null, minimal ? rightNavButtons : null]
      })
    })]
  });
};
const InlineProgressStepper = ({
  currentStep,
  handleStepChange,
  stepLabels,
  progressValue,
  sidebarState,
  buttonFns
}) => {
  const showNavButtons = false;
  const containerRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  useEffect(() => {
    if (containerRef.current) {
      setDropdownWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef]);
  return /* @__PURE__ */jsxs(XStack, {
    alignItems: "center",
    width: "100%",
    ref: containerRef,
    children: [showNavButtons ? /* @__PURE__ */jsxs(Group, {
      size: "$3",
      orientation: "horizontal",
      children: [/* @__PURE__ */jsx(Group.Item, {
        children: /* @__PURE__ */jsx(Button, {
          disabled: currentStep === 1,
          onPress: () => handleStepChange(1),
          size: "$1",
          icon: /* @__PURE__ */jsx(ChevronFirst, {})
        })
      }), /* @__PURE__ */jsx(Group.Item, {
        children: /* @__PURE__ */jsx(Button, {
          disabled: currentStep === 1,
          onPress: () => handleStepChange(currentStep - 1),
          size: "$1",
          icon: /* @__PURE__ */jsx(ChevronLeft, {})
        })
      })]
    }) : null, /* @__PURE__ */jsx(XStack, {
      className: "steppersSelect",
      alignItems: "center",
      style: {
        /* width: '98%', */
        flex: 1,
        padding: 0,
        /* margin: '5px', */
        alignItems: "center",
        overflow: "hidden"
      },
      children: /* @__PURE__ */jsx(DropDownPickerView, {
        setValue: stepString => handleStepChange(parseInt(stepString)),
        value: currentStep,
        items: stepLabels.map((label, idx) => ({
          label: `${idx + 1}. ${label}`,
          value: idx + 1
        })),
        DropDownContainerStyle: {
          width: "100%",
          padding: 0
          /* , margin: '10px 0' */
        },
        TriggerStyle: Platform.select({
          web: {
            padding: 0,
            border: "none",
            id: "inline-stepper-dropdown"
          },
          native: {
            fontSize: 14
          }
        }),
        ViewStyle: {
          padding: 0
        },
        bottomBorderColor: "white",
        ContainerStyles: [{
          padding: 0,
          width: dropdownWidth,
          height: 25
        }, {
          justifyContent: "flex-start",
          height: 25
        }, {
          height: 25
        }],
        native: true
      })
    }), showNavButtons ? /* @__PURE__ */jsxs(Group, {
      size: "$3",
      orientation: "horizontal",
      children: [/* @__PURE__ */jsx(Group.Item, {
        children: /* @__PURE__ */jsx(Button, {
          disabled: currentStep === stepLabels.length,
          onPress: () => handleStepChange(currentStep + 1),
          size: "$1",
          icon: /* @__PURE__ */jsx(ChevronRight, {})
        })
      }), /* @__PURE__ */jsx(Group.Item, {
        children: /* @__PURE__ */jsx(Button, {
          disabled: currentStep === stepLabels.length,
          onPress: () => handleStepChange(stepLabels.length),
          size: "$1",
          icon: /* @__PURE__ */jsx(ChevronLast, {})
        })
      })]
    }) : null]
  });
};
const styles = StyleSheet.create({
  container: {
    backgroundColor: "#85C19D",
    alignItems: "center",
    justifyContent: "center"
  },
  stepperContainer: {
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    flexGrow: 1
  },
  steps: {
    alignItems: "center"
  },
  circle: {
    width: 30,
    height: 30,
    borderRadius: 20,
    backgroundColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
    marginHorizontal: 2
  },
  activeCircle: {
    backgroundColor: "#fff"
  },
  circleText: {
    color: "#85C19D",
    fontWeight: "bold"
  },
  stepLabel: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
    marginHorizontal: 3
  }
});
export { InlineProgressStepper, ProgressStepper };
//# sourceMappingURL=ProgressStepper.mjs.map
